import React, { useState, useEffect, useRef } from "react";

export default function DateTimePicker({ label, value, onChange }) {
  const [show, setShow] = useState(false);
  const containerRef = useRef(null);

  const safeDate = value ? new Date(value) : new Date();
  const [selectedDate, setSelectedDate] = useState(safeDate);
  const [currentMonth, setCurrentMonth] = useState(
    new Date(safeDate.getFullYear(), safeDate.getMonth(), 1)
  );
  const [hour, setHour] = useState(safeDate.getHours());
  const [minute, setMinute] = useState(safeDate.getMinutes());

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    if (show) {
      document.addEventListener("mousedown", handleClick);
    }
    return () => document.removeEventListener("mousedown", handleClick);
  }, [show]);

  useEffect(() => {
    if (value) {
      const d = new Date(value);
      setSelectedDate(d);
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
      setHour(d.getHours());
      setMinute(d.getMinutes());
    }
  }, [value]);

  const handleApply = () => {
    const final = new Date(selectedDate);
    final.setHours(hour);
    final.setMinutes(minute);
    onChange(final);
    setShow(false);
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    setSelectedDate(newDate);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + direction,
        1
      )
    );
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const days = [];

    // Jours du mois précédent
    const startDay = firstDay === 0 ? 6 : firstDay - 1;
    for (let i = startDay; i > 0; i--) {
      days.push({
        day: daysInPrevMonth - i + 1,
        isCurrentMonth: false,
        isPrev: true,
      });
    }

    // Jours du mois actuel
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true });
    }

    // Jours du mois suivant
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false, isNext: true });
    }

    return days;
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day.isCurrentMonth &&
      day.day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day) => {
    return (
      day.isCurrentMonth &&
      day.day === selectedDate.getDate() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      currentMonth.getFullYear() === selectedDate.getFullYear()
    );
  };

  const formatDisplayDate = () => {
    if (!value) return "Sélectionner...";
    const d = new Date(value);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const days = getDaysInMonth();

  return (
    <div className="w-full relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-primary mb-1">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => setShow(!show)}
        className="w-full text-left px-4 py-2 bg-white border border-gray-300 rounded-lg text-primary outline-none focus:border-yellow-400 transition-colors"
      >
        {formatDisplayDate()}
      </button>

      {show && (
        <div className="absolute mt-2 z-[999] bg-white shadow-2xl border border-gray-200 rounded-2xl overflow-hidden w-[350px]">
          {/* Header */}
          <div className="bg-ananas p-4 flex justify-between items-center text-primary">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-black/5 rounded-full transition"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <span className="font-bold capitalize text-lg">
              {currentMonth.toLocaleString("fr-FR", {
                month: "long",
                year: "numeric",
              })}
            </span>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-black/5 rounded-full transition"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>

          <div className="p-4">
            {/* Calendrier */}
            <div className="mb-6">
              {/* En-têtes des jours */}
              <div className="grid grid-cols-7 mb-2">
                {["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"].map(
                  (day) => (
                    <div
                      key={day}
                      className="text-center text-primary font-bold text-[0.7rem] uppercase"
                    >
                      {day}
                    </div>
                  )
                )}
              </div>

              {/* Grille des jours */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((dayObj, idx) => {
                  const selected = isSelected(dayObj);
                  const today = isToday(dayObj);

                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() =>
                        dayObj.isCurrentMonth && handleDateSelect(dayObj.day)
                      }
                      disabled={!dayObj.isCurrentMonth}
                      className={`
                        h-10 w-10 flex items-center justify-center rounded-full font-bold text-sm transition-all
                        ${
                          !dayObj.isCurrentMonth
                            ? "text-muted/60 cursor-default"
                            : "text-primary cursor-pointer hover:bg-gray-100"
                        }
                        ${
                          selected
                            ? "bg-ananas text-primary hover:bg-yellow-400"
                            : ""
                        }
                        ${
                          today && !selected
                            ? "bg-text-primary text-white hover:text-primary"
                            : ""
                        }
                      `}
                    >
                      {dayObj.day}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sélecteurs de temps */}
            <div className="flex gap-3 items-center justify-center border-t border-gray-100 pt-4 mb-4">
              <div className="relative">
                <select
                  value={hour}
                  onChange={(e) => setHour(parseInt(e.target.value))}
                  className="bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-8 py-2 w-24 outline-none text-primary font-bold appearance-none cursor-pointer focus:border-yellow-400"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {i.toString().padStart(2, "0")}h
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-primary">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </div>
              </div>

              <div className="relative">
                <select
                  value={minute}
                  onChange={(e) => setMinute(parseInt(e.target.value))}
                  className="bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-8 py-2 w-24 outline-none text-primary font-bold appearance-none cursor-pointer focus:border-yellow-400"
                >
                  {Array.from({ length: 4 }, (_, i) => i * 15).map((m) => (
                    <option key={m} value={m}>
                      {m.toString().padStart(2, "0")}m
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-primary">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShow(false)}
                className="flex-1 py-2.5 font-bold bg-gray-100 text-primary rounded-full hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="flex-1 py-2.5 font-bold bg-gray-900 text-white rounded-full shadow-lg hover:opacity-90 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Demo
function Demo() {
  const [date1, setDate1] = useState(new Date());
  const [date2, setDate2] = useState(null);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-2xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-primary mb-2">
            DateTimePicker
          </h1>
          <p className="text-gray-600">
            Un composant fonctionnel avec design propre
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border  p-6">
          <DateTimePicker
            label="Date de publication"
            value={date1}
            onChange={setDate1}
          />

          {date1 && (
            <div className="mt-4 text-sm text-primary">
              Date sélectionnée: {date1.toLocaleString("fr-FR")}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <DateTimePicker
            label="Date de début"
            value={date2}
            onChange={setDate2}
          />

          {date2 && (
            <div className="mt-4 text-sm text-gray-600">
              Date sélectionnée: {date2.toLocaleString("fr-FR")}
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <strong>Changements:</strong>
          <ul className="mt-2 space-y-1 ml-4 list-disc">
            <li>Calendrier complètement refait sans dépendances externes</li>
            <li>Jour sélectionné: bg-yellow-400 (ananas) + text-gray-900</li>
            <li>Jour actuel: bg-gray-900 + text-white</li>
            <li>Navigation fonctionnelle entre les mois</li>
            <li>Grille de 7x6 jours (42 cases)</li>
            <li>Gestion correcte des jours précédents/suivants</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
